import { useTranslation } from "react-i18next";
import { IconBooks, IconPaperclip, IconStack2 } from "@tabler/icons-react";
import BottomPanel from "../app/panels/BottomPanel";
import { DesktopDialog } from "../app/DesktopDialog";
import { cn } from "../../lib/utils";
import { createMemoryStore } from "../../lib/store";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/Button";
import { Tag } from "../ui/Tag";
import { RoomSupportedByChip } from "./RoomSupportedByChip";
import { RoomContactLine } from "./RoomContactLine";
import type { Api } from "../../services/api";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import {
  clientDisclaimer,
  contactLineKind,
  foldDisclaimer,
} from "../../domains/partners/roomWelcome";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import {
  DISCLAIMER_FOLD_LENGTH,
  defaultRoomDisclaimer,
  fillDisclaimerVariables,
} from "../../../../api/src/shared/domain/room-disclaimer";
import type { RoomDiscoveryChannel } from "../../tools/analytics/domains/invite.analytics";
import {
  gaEventRoomWelcomeCompleted,
  gaEventRoomWelcomeShown,
} from "../../tools/analytics/domains/partners.analytics";
import { roomContentCounts } from "../../domains/room/utils/roomContentCounts";

type PrivateRoom = Api["get"]["/webapp/private/rooms/:id"]["output"]["body"];
/** The fields of the room the sheet reads. */
export type WelcomeRoom = Pick<
  PrivateRoom,
  | "id"
  | "translations"
  | "organisationModel"
  | "icoModel"
  | "partnerContact"
  | "privacyMarketingSettings"
  | "filterTopicModels"
  | "mainSpecialtyModel"
  | "tagModels"
  | "contentCounts"
>;

// The room whose disclaimer is unfolded, if any. Keyed by room so that a
// welcome that replaces another one opens folded, and reset when the sheet
// closes.
const useExpandedDisclaimerRoom = createMemoryStore<string | null>(null);

/** Topic chips before "+N": two lines on a phone (Figma O3, cell 04). */
const VISIBLE_TOPICS = 6;

/**
 * The welcome sheet (Figma O3, docs/partner-objection.md REQ-05, REQ-06): the
 * one sheet after a join, on every entry. The join is recorded before it
 * opens, so closing it never removes the membership (INV-05). It collects
 * nothing: the client disclaimer, what the room contains, and the contact
 * line, fixed at the bottom with the button while the top scrolls. A desktop
 * shows the same welcome in a dialog (Figma D3, 4958:108617).
 */
export function RoomWelcomeSheet({
  room,
  roomDiscoveryChannel,
  presentation,
  isOpen,
  onClose,
}: {
  room: WelcomeRoom;
  roomDiscoveryChannel: RoomDiscoveryChannel | null;
  /** A bottom sheet on a phone, a dialog on a desktop (Figma D3). */
  presentation: "sheet" | "dialog";
  isOpen: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { activeLang, contentLang } = useActiveLang();
  const counts = roomContentCounts(room, contentLang);
  const [expandedDisclaimerRoom, setExpandedDisclaimerRoom] =
    useExpandedDisclaimerRoom();
  const isDisclaimerExpanded = expandedDisclaimerRoom === room.id;

  const roomName = getRoomName(room, activeLang);
  const partnerName = room.organisationModel.name;
  const hasPartner = room.partnerContact !== "none";
  const contactLine = contactLineKind(room.partnerContact, true);
  if (contactLine === "conditional")
    throw new Error(
      "should not happen: a member's contact line is conditional"
    );

  const client = clientDisclaimer(room, activeLang, { roomName, partnerName });
  // The default text is legal copy, written once for the webapp and the admin
  // preview (`room-disclaimer.ts`), not a UI translation.
  const disclaimer =
    client !== null
      ? client
      : fillDisclaimerVariables(defaultRoomDisclaimer(activeLang, hasPartner), {
          roomName,
          partnerName,
        });
  const { shown, folded } = foldDisclaimer(disclaimer, DISCLAIMER_FOLD_LENGTH);

  // The chips are the room topics, and the room specialties when it has none
  // (Figma O3, cell 06): the main one first.
  const topics =
    room.filterTopicModels.length > 0
      ? room.filterTopicModels.map((topic) => getItemTranslation(topic))
      : [
          ...(room.mainSpecialtyModel === null
            ? []
            : [getItemTranslation(room.mainSpecialtyModel)]),
          ...room.tagModels
            .filter((tag) =>
              room.mainSpecialtyModel === null
                ? true
                : tag.id !== room.mainSpecialtyModel.id
            )
            .map((tag) => getItemTranslation(tag)),
        ];
  const hiddenTopics = topics.length - VISIBLE_TOPICS;

  useTrackOnMount(
    "room-welcome-shown",
    isOpen ? { status: "shown", key: room.id } : { status: "hidden" },
    () => {
      gaEventRoomWelcomeShown({
        roomId: room.id,
        roomDiscoveryChannel,
        hasClientDisclaimer: client !== null,
        contactLine,
      });
    }
  );

  function close(exit: "discover" | "closed"): void {
    gaEventRoomWelcomeCompleted({
      roomId: room.id,
      exit,
      disclaimerExpanded: isDisclaimerExpanded,
    });
    setExpandedDisclaimerRoom(null);
    onClose();
  }

  const logoUrl = getRoomLogoUrl(room);
  const isDialog = presentation === "dialog";
  const title = t("You have joined the {{room}} Room", { room: roomName });

  const supportedBy = hasPartner ? (
    <RoomSupportedByChip organisationName={partnerName} size="md" />
  ) : null;

  const phoneHeader = (
    <>
      {logoUrl.length > 0 && (
        <Avatar
          variant="image"
          src={logoUrl}
          alt={roomName}
          imageFit="scale-down"
          size="lg"
          badge={null}
        />
      )}
      <h2 className="m-0 font-dm-sans text-heading-lg font-semibold text-ds-mint-900">
        {title}
      </h2>
      {supportedBy}
    </>
  );

  const content = (
    <>
      <p
        className={cn(
          "m-0 whitespace-pre-wrap font-dm-sans",
          isDialog
            ? "text-sm text-ds-slate-600"
            : "text-ui-body text-ds-slate-700"
        )}
      >
        {isDisclaimerExpanded || folded === null ? disclaimer : `${shown} `}
        {folded !== null && !isDisclaimerExpanded && (
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 font-medium text-ds-mint-700"
            onClick={() => {
              setExpandedDisclaimerRoom(room.id);
            }}
          >
            {t("See more")}
          </button>
        )}
      </p>

      {/* Figma Desktop 5588:229733: 16px under the disclaimer on a desktop. */}
      <div className={cn("flex w-full flex-col gap-2.5", !isDialog && "pt-1")}>
        <span
          className={cn(
            "font-dm-sans uppercase",
            isDialog
              ? "text-desktop-overline font-medium text-ds-slate-600"
              : "text-caption font-semibold tracking-wider text-ds-slate-700"
          )}
        >
          {t("What's inside")}
        </span>
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {[
              ...topics.slice(0, VISIBLE_TOPICS),
              ...(hiddenTopics > 0 ? [`+${hiddenTopics}`] : []),
            ].map((topic) =>
              isDialog ? (
                <Tag
                  key={topic}
                  size="md"
                  variant="neutral"
                  state="idle"
                  icon={null}
                >
                  {topic}
                </Tag>
              ) : (
                <span
                  key={topic}
                  className="rounded bg-ds-black/4 px-2 py-1 font-dm-sans text-footnote font-medium text-ds-slate-600"
                >
                  {topic}
                </span>
              )
            )}
          </div>
        )}
        <div className={cn("flex flex-col", isDialog ? "gap-2.5" : "gap-2")}>
          {hasPartner && (
            <WelcomeFeature
              presentation={presentation}
              Icon={IconPaperclip}
              title={t("Resources selected by {{partner}}", {
                partner: partnerName,
              })}
              subtitle={null}
            />
          )}
          <WelcomeFeature
            presentation={presentation}
            Icon={IconBooks}
            title={t("Latest publications in {{room}}", { room: roomName })}
            subtitle={
              counts === null
                ? null
                : t("Curated by Juisci · {{n}} articles available", {
                    n: counts.articles.toLocaleString(activeLang),
                  })
            }
          />
          <WelcomeFeature
            presentation={presentation}
            Icon={IconStack2}
            title={t("Thematic collections")}
            subtitle={null}
          />
        </div>
      </div>
    </>
  );

  const footer = (
    <>
      {contactLine !== "none" && (
        <RoomContactLine
          kind={contactLine}
          partnerName={partnerName}
          presentation={presentation}
        />
      )}
      <Button
        variant="primary"
        size={isDialog ? "md" : "lg"}
        // Figma Desktop 5588:229733 draws the dialog's button 40px high with a
        // 13px semibold label, between the DS sm (36) and md (48).
        className={cn("w-full", isDialog && "h-10 font-semibold")}
        onClick={() => {
          close("discover");
        }}
      >
        {t("Discover the room")}
      </Button>
    </>
  );

  if (isDialog) {
    if (!isOpen) return null;
    // Figma Desktop 5588:229733: the room's logo on the close button's row,
    // the "Supported by" badge under the title.
    return (
      <DesktopDialog
        presentation="modal"
        title={title}
        header={{
          layout: "stacked",
          leading:
            logoUrl.length > 0 ? (
              <img
                src={logoUrl}
                alt={roomName}
                className="size-11 shrink-0 rounded-lg object-contain"
              />
            ) : null,
        }}
        subtitle={supportedBy}
        onClose={() => {
          close("closed");
        }}
        footer={footer}
      >
        {content}
      </DesktopDialog>
    );
  }

  return (
    <BottomPanel
      isOpen={isOpen}
      onClose={() => {
        close("closed");
      }}
      backdropBlur
      displayCloseButton
      disableBottomPadding
      // Figma O3 (4796:7392): the sheet takes 90% of the screen, and the
      // contact line and the button keep the 24px side margin of the content.
      // The panel's own action bar has a wider margin, so the footer is a
      // sticky block of the scroll box (as in AnnotationSheet).
      height="90dvh"
    >
      <div className="flex flex-col items-start gap-2.5 px-6 pt-11">
        {phoneHeader}
        {content}
      </div>
      <div
        className="sticky bottom-0 mt-auto flex flex-col gap-3.5 bg-white px-6 pt-4"
        style={{ paddingBottom: "calc(16px + var(--safe-area-bottom))" }}
      >
        {footer}
      </div>
    </BottomPanel>
  );
}

function WelcomeFeature({
  presentation,
  Icon,
  title,
  subtitle,
}: {
  presentation: "sheet" | "dialog";
  Icon: typeof IconBooks;
  title: string;
  subtitle: string | null;
}): React.JSX.Element {
  const isDialog = presentation === "dialog";
  return (
    <div className={cn("flex items-center", isDialog ? "gap-3" : "gap-2.5")}>
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-lg",
          isDialog ? "bg-ds-slate-100" : "bg-ds-black/6"
        )}
      >
        <Icon className="size-4 text-ds-slate-700" stroke={1.5} />
      </span>
      <span
        className={cn("flex min-w-0 flex-1 flex-col", isDialog && "gap-0.5")}
      >
        <span
          className={cn(
            "font-dm-sans text-sm font-medium",
            isDialog ? "text-ds-black" : "text-ds-slate-900"
          )}
        >
          {title}
        </span>
        {subtitle !== null && (
          <span
            className={cn(
              "font-dm-sans text-ds-slate-600",
              isDialog ? "text-desktop-meta" : "text-caption"
            )}
          >
            {subtitle}
          </span>
        )}
      </span>
    </div>
  );
}
